import type { IconButtonSize } from "../icon-button";
import type { TypographyProps } from "../typography";
import type { ActionButtonVariant } from "./types";

type TypographyColor = NonNullable<TypographyProps["color"]>;
type TypographyVariant = NonNullable<TypographyProps["variant"]>;

/** Label typography variant per size — caption-sm / caption-md / body-sm. */
export const actionButtonLabelVariant: Record<IconButtonSize, TypographyVariant> = {
  sm: "caption-sm",
  md: "caption-md",
  lg: "body-sm",
};

/**
 * Label color per variant + state.
 *
 * The label sits OUTSIDE the pill on the page surface, so it uses neutral
 * foreground colors — except destructive variants, where red text reinforces
 * intent: `button.destructive.onSurface` #a01414 (split rows 83 and 89). The
 * neutral disabled caption matches its icon, #a9b9cc (split row 87).
 */
export const actionButtonLabelColor: Record<
  ActionButtonVariant,
  { default: TypographyColor; disabled: TypographyColor }
> = {
  primary: {
    default: "neutral-foreground-default",
    disabled: "button-disabled-cta-text-disabled",
  },
  secondary: {
    default: "neutral-foreground-default",
    disabled: "button-disabled-cta-text-disabled",
  },
  tertiary: {
    default: "neutral-foreground-default",
    disabled: "button-disabled-cta-text-disabled",
  },
  neutral: {
    default: "neutral-foreground-default",
    disabled: "neutral-foreground-attenuated",
  },
  destructive: {
    default: "button-destructive-on-surface",
    disabled: "button-disabled-cta-text-disabled",
  },
  "destructive-secondary": {
    default: "button-destructive-on-surface",
    disabled: "button-disabled-cta-text-disabled",
  },
};

/**
 * Pill look per variant (same tokens as IconButton's `buttonVariants`). The
 * pill is a picture inside the tile's Pressable, so its hover and pressed looks
 * follow the tile (`group-hover:` / `group-active:`) on both platforms. A
 * `hover:` or `active:` class on the pill's own View would make NativeWind turn
 * it into a second Pressable that takes the tap.
 */
export const actionButtonPillClass: Record<
  ActionButtonVariant,
  { default: string; disabled: string }
> = {
  primary: {
    default:
      "bg-luce-button-cta-default border-0 group-hover:bg-luce-button-cta-hover group-active:bg-luce-button-cta-pressed",
    disabled: "bg-luce-button-disabled-ctaDisabled border-0",
  },
  secondary: {
    default: [
      "border-luce-button-cta2-border border border-solid bg-transparent",
      "group-hover:border-luce-button-cta2-borderHover group-hover:bg-luce-button-cta2-surfaceHover",
      "group-active:border-luce-button-cta2-borderHover group-active:bg-luce-button-cta2-surfacePressed",
    ].join(" "),
    disabled: "border-luce-button-disabled-ctaBorderDisabled border border-solid bg-transparent",
  },
  tertiary: {
    default:
      "border-0 bg-transparent group-hover:bg-luce-button-cta2-surfaceHover group-active:bg-luce-button-cta2-surfacePressed",
    disabled: "border-0 bg-transparent",
  },
  neutral: {
    default: [
      "border-luce-neutral-border-mediumEmphasis bg-luce-neutral-background-default border border-solid",
      "group-hover:bg-luce-neutral-background-intermediate group-active:bg-luce-neutral-background-attenuated",
    ].join(" "),
    disabled:
      "border-luce-button-disabled-ctaBorderDisabled bg-luce-button-disabled-ctaDisabled border border-solid",
  },
  destructive: {
    default:
      "bg-luce-button-destructive-default border-0 group-hover:bg-luce-button-destructive-hover group-active:bg-luce-button-destructive-pressed",
    disabled: "bg-luce-button-disabled-ctaDisabled border-0",
  },
  "destructive-secondary": {
    default: [
      "border-luce-button-destructive-border border border-solid bg-transparent",
      "group-hover:border-luce-button-destructive-borderHover group-hover:bg-luce-button-destructive-surface",
      "group-active:border-luce-button-destructive-borderHover group-active:bg-luce-button-destructive-surfacePressed",
    ].join(" "),
    disabled: "border-luce-button-disabled-ctaBorderDisabled border border-solid bg-transparent",
  },
};

/**
 * The keyboard focus ring, on the pill (DS-040): the tile has focus, so the
 * pill draws it through the tile's group, on web only, like RadioGroup's dot.
 */
export const actionButtonPillFocusClass =
  "web:group-focus-visible:ring-focus web:group-focus-visible:ring-luce-brand-primaryBorder web:group-focus-visible:ring-offset-focus";

/**
 * Pill box per size, from tokens: the `_cta` height and a width of the icon
 * line plus its padding (DS-040): 36×24 / 48×32 / 56×40, and 48×32 / 72×40 /
 * 76×48 on desktop. A fixed border-box, so a bordered variant is no wider.
 */
export const actionButtonPillSizeClass: Record<IconButtonSize, string> = {
  sm: "h-cta-sm w-action-pill-sm md:h-cta-sm-dt md:w-action-pill-sm-dt",
  md: "h-cta-md w-action-pill-md md:h-cta-md-dt md:w-action-pill-md-dt",
  lg: "h-cta-lg w-action-pill-lg md:h-cta-lg-dt md:w-action-pill-lg-dt",
};

/**
 * Caption colour while the tile is hovered or pressed (DS-040, Figma
 * 3050:8461, 3401:12821, 486:14929, 3401:13120): neutral intermediate. A
 * destructive caption keeps #a01414 in every enabled state (split row 83).
 */
export const actionButtonLabelStateClass: Record<ActionButtonVariant, string> = {
  primary:
    "group-hover:text-luce-neutral-foreground-intermediate group-active:text-luce-neutral-foreground-intermediate",
  secondary:
    "group-hover:text-luce-neutral-foreground-intermediate group-active:text-luce-neutral-foreground-intermediate",
  tertiary:
    "group-hover:text-luce-neutral-foreground-intermediate group-active:text-luce-neutral-foreground-intermediate",
  neutral:
    "group-hover:text-luce-neutral-foreground-intermediate group-active:text-luce-neutral-foreground-intermediate",
  destructive: "",
  "destructive-secondary": "",
};
